import React, { useEffect } from "react";

import { capitalize } from "lodash";
import { useInView } from "react-intersection-observer";

import {
  ButtonIcon,
  CollapsibleCard,
  Heading,
  IconXMark,
  Label
} from "@busbud/horizon";
import type { Amenity } from "@busbud/int-schemas";
import type { Fare } from "@busbud/int-schemas/udm";

import { getDisplayableAmenityKeys } from "@app/helpers/amenities";
import { useAppContext } from "@app/helpers/hooks";
import { AsyncCartItinerary } from "@app/modules/async-cart/components/async-cart-itinerary.connect";
import type { Direction } from "@app/modules/results/components/departure/seat-selection-details/seat-selector/seat-selector";
import { ViewTermsAndConditions } from "@app/tracking/drawer-tracking";

import type { TermsProps } from "../summary-cards/summary-card.props";

import { AmenitiesChips } from "../amenities-chips";
import { TermsFullContent } from "../terms-and-conditions/components";

type Props = {
  departure_id: string;
  terms_props: TermsProps;
  direction: Direction;
  is_roundtrip: boolean;
  fare: Fare;
  amenities: Partial<Record<Amenity, boolean>>;
  onClose: () => void;
};

export const SummaryDrawer = ({
  departure_id,
  terms_props,
  direction,
  is_roundtrip,
  fare,
  amenities,
  onClose
}: Props) => {
  const { translator, tracker } = useAppContext();

  const displayed_direction =
    direction === "outbound"
      ? translator.t("!departure.type.outbound")
      : translator.t("!departure.type.return");

  const { ref, inView } = useInView();
  useEffect(() => {
    if (!!inView) {
      tracker.track(ViewTermsAndConditions(direction));
    }
  }, [inView, tracker, direction]);

  const supported_amenities = getDisplayableAmenityKeys(Object.keys(amenities));

  return (
    <div>
      <div className="flex items-center justify-between pb-200 pl-250 pr-200 pt-250">
        <Heading as="h3" size="sm">
          {translator.t("!common.summary-card.drawer.title")}
        </Heading>
        <ButtonIcon variant="text" onClick={onClose}>
          <IconXMark size="md" />
        </ButtonIcon>
      </div>
      <div className="-space-y-500 *:pb-500 [&>:last-child]:pb-250">
        <CollapsibleCard
          size="sm"
          withTransition
          renderHeader={headerProps => (
            <CollapsibleCard.Header
              {...headerProps}
              buttonAriaLabel={translator.t(
                "!accounts.settings.expand-content"
              )}
            >
              <Heading as="span" size="xs">
                {translator.t("!checkout.departures.your-trip")}
              </Heading>
            </CollapsibleCard.Header>
          )}
          isInitiallyExpanded={true}
        >
          <CollapsibleCard.Body>
            <AsyncCartItinerary trip_id={departure_id} direction={direction} />
          </CollapsibleCard.Body>
        </CollapsibleCard>
        <CollapsibleCard
          size="sm"
          withTransition
          renderHeader={headerProps => (
            <CollapsibleCard.Header
              {...headerProps}
              buttonAriaLabel={translator.t(
                "!accounts.settings.expand-content"
              )}
            >
              <Heading as="span" size="xs">
                {translator.t(
                  "!common.summary-card.drawer.comfort-flexibility"
                )}
              </Heading>
            </CollapsibleCard.Header>
          )}
          isInitiallyExpanded={true}
        >
          <CollapsibleCard.Body>
            {!!is_roundtrip && (
              <Label size="lg" fontWeight="bold" as="span">
                {displayed_direction}&nbsp;
              </Label>
            )}
            <Label as="span" size="lg">
              {capitalize(fare.name)}
            </Label>
            <AmenitiesChips amenities={supported_amenities} />
          </CollapsibleCard.Body>
        </CollapsibleCard>
        <CollapsibleCard
          size="sm"
          withTransition
          renderHeader={headerProps => (
            <CollapsibleCard.Header
              {...headerProps}
              buttonAriaLabel={translator.t(
                "!accounts.settings.expand-content"
              )}
            >
              <Heading as="span" size="xs">
                {translator.t("!terms.module.title")}
              </Heading>
            </CollapsibleCard.Header>
          )}
          isInitiallyExpanded={true}
        >
          <CollapsibleCard.Body>
            <div ref={ref}>
              <TermsFullContent {...terms_props} />
            </div>
          </CollapsibleCard.Body>
        </CollapsibleCard>
      </div>
    </div>
  );
};
